import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  applyEdgeChanges,
  applyNodeChanges,
  type Connection,
  type Edge,
  type EdgeMouseHandler,
  type Node,
  type OnConnect,
  type OnEdgesChange,
  type OnNodesChange,
} from "reactflow";
import { Loader2, Sparkles, Trash2, Upload, Save, X } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import ExperimentalFeatureBanner from "components/Common/ExperimentalFeatureBanner";
import IssueSearch, { type Suggestion } from "components/Issues/IssueSearch";
import GraphAnalysisPanel from "components/Graph/GraphAnalysisPanel";
import GraphCanvas from "components/Graph/GraphCanvas";
import { FULLSCREEN_VIEWER_BODY_CLASS } from "ui/FullscreenViewer";
import GraphAddInfluenceSurface from "components/Graph/GraphAddInfluenceSurface";
import CollectionPostForm, { type CollectionFormHandle } from "components/Posts/CollectionPostForm";
import CommentInput, { type CommentInputContext } from "components/Comments/CommentInput";
import type { CoauthorSuggestion } from "components/Coauthors/CoauthorSelect";
import { formatDate, formatRelativeTime } from "config/dayjsConfig";
import {
  formatGraphMessage,
  graphMessage,
  resolveGraphLocale,
  type GraphLocale,
} from "components/Graph/graphCopy";
import {
  type GraphCanvasEdgeData,
  type GraphEdgeNoteSummary,
  type GraphSourceDescriptor,
  buildGraphRelationEdgeId,
  graphCollectionsApi,
  mapGraphSnapshotToCanvasData,
  type GraphCollectionSnapshot,
} from "lib/api/graphCollections";
import { api } from "lib/api/client";
import { collectionApi } from "lib/api/collections";
import { useUser } from "util/UserContext";
import { useIssueFromSuggestion } from "hooks/useIssueFromSuggestion";
import useMobileViewport from "hooks/useMobileViewport";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { useEdgeSelection } from "components/Graph/useEdgeSelection";
import LiveAnnouncer from "ui/LiveAnnouncer";
import { toastUndo, toastSuccessWithUndo } from "ui/toastUndo";
import { getAiUiStrings, resolveAiLocale } from "i18n/aiUi";
import type { PollDraft } from "types/poll";
import type { Issue } from "util/constants";
import type { GraphIssueNodeData } from "components/Graph/GraphIssueNodeTypes";
import { transformHtmlContent } from "util/transformHtmlContent";
import { uploadEntityMediaDirect } from "util/entityMediaUpload";
import { createCollection, type CollectionPostResponse } from "util/posts";
import { PrivacyLevel } from "util/userUtils";
import { apiRoutes } from "util/apiRoutes";
import { toast } from "sonner";
import InputField from "ui/InputField";
import DetailPanel from "ui/DetailPanel";
import DialogConfirmation from "ui/DialogConfirmation";
import LoadingSkeleton from "ui/LoadingSkeleton";
import { LAYER_TOKENS } from "ui/layers";

const INITIAL_SEED_POSITION = { x: 0, y: 0 };
const NEW_CONNECTION_OFFSET_X = 320;
const POSITION_SAVE_DEBOUNCE_MS = 700;
/*
 * How long (ms) we keep the "just added" marker on a newly-created edge
 * before it reverts to the default rest state. All visual styling for
 * new / focused / neighborhood edges lives in `GraphInfluenceEdge`.
 */
const NEW_EDGE_HIGHLIGHT_MS = 30_000;
const LOOSE_NODE_GAP_X = 240;
const LOOSE_NODE_GAP_Y = 180;
const LOOSE_NODE_COLUMNS = 3;
/**
 * Minimum distance between two node origins before we treat a candidate slot
 * as "occupied". Node cards are ~200px wide so a 220/160 collision box gives
 * them breathing room without excessive whitespace.
 */
const NODE_SLOT_WIDTH = 220;
const NODE_SLOT_HEIGHT = 160;
const MAX_SLOT_SEARCH_ATTEMPTS = 40;
const noopDepth = () => undefined;

/**
 * Returns true when the candidate position overlaps with any existing node's
 * position within the node-slot collision box.
 */
const isPositionOccupied = (
  candidateX: number,
  candidateY: number,
  existingNodes: Array<Node<GraphIssueNodeData>>,
  excludeNodeId?: string
): boolean => {
  return existingNodes.some((node) => {
    if (excludeNodeId && node.id === excludeNodeId) {
      return false;
    }
    return (
      Math.abs(node.position.x - candidateX) < NODE_SLOT_WIDTH &&
      Math.abs(node.position.y - candidateY) < NODE_SLOT_HEIGHT
    );
  });
};

/**
 * Finds the next empty slot for a newly added influencer/influenced node. We
 * anchor to the center node's y coordinate and probe outward (center first,
 * then alternating up/down) so added nodes fall into the most visually natural
 * open row relative to the influence relationship.
 */
const findFreeInfluencePosition = (
  centerPosition: { x: number; y: number },
  direction: "influencer" | "influenced",
  existingNodes: Array<Node<GraphIssueNodeData>>
): { x: number; y: number } => {
  const offsetX =
    direction === "influencer" ? -NEW_CONNECTION_OFFSET_X : NEW_CONNECTION_OFFSET_X;
  const baseX = centerPosition.x + offsetX;
  const baseY = centerPosition.y;

  for (let attempt = 0; attempt < MAX_SLOT_SEARCH_ATTEMPTS; attempt += 1) {
    let candidateY = baseY;
    if (attempt > 0) {
      const stepSign = attempt % 2 === 1 ? 1 : -1;
      const stepMagnitude = Math.ceil(attempt / 2) * NODE_SLOT_HEIGHT;
      candidateY = baseY + stepSign * stepMagnitude;
    }
    if (!isPositionOccupied(baseX, candidateY, existingNodes)) {
      return { x: baseX, y: candidateY };
    }
  }

  // Fallback: stack below all current nodes in the target column.
  const columnYs = existingNodes
    .filter((node) => Math.abs(node.position.x - baseX) < NODE_SLOT_WIDTH)
    .map((node) => node.position.y);
  const maxColumnY = columnYs.length > 0 ? Math.max(...columnYs) : baseY;
  return { x: baseX, y: maxColumnY + NODE_SLOT_HEIGHT };
};

const parsePositiveInt = (value: string | null): number | null => {
  const parsed = Number.parseInt(String(value || ""), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const getGraphSeedLabel = (
  issue: Issue | Suggestion | null,
  locale: GraphLocale
): string => {
  if (!issue) {
    return graphMessage(locale, "untitledGraph");
  }
  const rawName = typeof issue.name === "string" && issue.name.trim().length > 0
    ? issue.name
    : issue.cname;
  return String(rawName || graphMessage(locale, "untitledGraph")).replace(/_/g, " ");
};

interface EdgeCounts {
  inCount: number;
  outCount: number;
}

const computeEdgeCounts = (edgeList: Array<Pick<Edge, "source" | "target">>): Record<string, EdgeCounts> => {
  const counts: Record<string, EdgeCounts> = {};
  edgeList.forEach((edge) => {
    if (!counts[edge.source]) {
      counts[edge.source] = { inCount: 0, outCount: 0 };
    }
    if (!counts[edge.target]) {
      counts[edge.target] = { inCount: 0, outCount: 0 };
    }
    counts[edge.source].outCount += 1;
    counts[edge.target].inCount += 1;
  });
  return counts;
};

/**
 * Finds a free slot below the existing graph bounding box for a standalone
 * (non-influence) add. We sweep column-by-column across a LOOSE_NODE_COLUMNS
 * grid anchored to the current left edge so repeated adds never stack on top
 * of each other, and we fall back to a guaranteed-empty row under the lowest
 * known node if the search grid is exhausted.
 */
const getNextLooseIssuePosition = (
  currentNodes: Array<Node<GraphIssueNodeData>>
): { x: number; y: number } => {
  if (currentNodes.length === 0) {
    return INITIAL_SEED_POSITION;
  }

  const minX = Math.min(...currentNodes.map((node) => node.position.x));
  const maxY = Math.max(...currentNodes.map((node) => node.position.y));

  for (let row = 1; row <= MAX_SLOT_SEARCH_ATTEMPTS; row += 1) {
    for (let column = 0; column < LOOSE_NODE_COLUMNS; column += 1) {
      const candidateX = minX + column * LOOSE_NODE_GAP_X;
      const candidateY = maxY + row * LOOSE_NODE_GAP_Y;
      if (!isPositionOccupied(candidateX, candidateY, currentNodes)) {
        return { x: candidateX, y: candidateY };
      }
    }
  }

  return { x: minX, y: maxY + LOOSE_NODE_GAP_Y * 2 };
};

interface GraphSelectedEdge {
  id: string;
  relationId: number | null;
  sourceName: string;
  targetName: string;
  note: GraphEdgeNoteSummary | null;
  notes: GraphEdgeNoteSummary[];
  isNew: boolean;
}

interface GraphEdgeInspectorProps {
  edge: GraphSelectedEdge;
  locale: GraphLocale;
  onClose: () => void;
  /**
   * When provided, renders a destructive "Remove influence from this graph"
   * action in the inspector footer. Clicking it should detach the influence
   * (with Undo handled by the caller). Omit for read-only surfaces.
   */
  onRemoveInfluence?: () => void;
  /**
   * When provided, renders a "delete" affordance next to each note the
   * viewer authored. The callback receives the note id.
   */
  onDeleteNote?: (noteId: number) => void;
  /**
   * Viewer's user id; notes owned by this user gain inline edit/delete
   * controls.
   */
  viewerUserId?: number | null;
}

const NoteRow: React.FC<{
  note: GraphEdgeNoteSummary;
  locale: GraphLocale;
  canManage: boolean;
  onDelete?: () => void;
}> = ({ note, locale, canManage, onDelete }) => {
  const timestamp = note.updated_at || note.created_at || null;
  const placeLabel =
    note.properties?.place?.formatted_address
    ?? note.properties?.place?.name
    ?? null;
  const timelineTs = note.properties?.timeline_ts ?? null;
  return (
    <article className="flex flex-col gap-1 rounded-xl bg-eoe-bg/60 p-2 dark:bg-eoe-dark-bg/60">
      <div className="flex items-start justify-between gap-2 text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
        <span className="truncate">
          {note.username ? `@${note.username}` : ""}
          {note.username && timestamp ? " · " : ""}
          {timestamp ? formatRelativeTime(timestamp) : ""}
        </span>
        {canManage && onDelete ? (
          <button
            type="button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onDelete();
            }}
            className="text-[11px] font-semibold text-warning-red hover:underline dark:text-dark-warning-red"
            aria-label={graphMessage(locale, "deleteNote")}
          >
            {graphMessage(locale, "deleteNote")}
          </button>
        ) : null}
      </div>
      <div className="prose prose-sm max-w-none break-words text-eoe-text dark:prose-invert dark:text-eoe-dark-text">
        {transformHtmlContent(note.content)}
      </div>
      {timelineTs ? (
        <div className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
          <span className="font-semibold text-eoe-text dark:text-eoe-dark-text">
            {`${graphMessage(locale, "edgeInspectorTimeline")}: `}
          </span>
          {formatDate(timelineTs)}
        </div>
      ) : null}
      {placeLabel ? (
        <div className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
          <span className="font-semibold text-eoe-text dark:text-eoe-dark-text">
            {`${graphMessage(locale, "edgeInspectorPlace")}: `}
          </span>
          {placeLabel}
        </div>
      ) : null}
    </article>
  );
};

const GraphEdgeInspector: React.FC<GraphEdgeInspectorProps> = ({
  edge,
  locale,
  onClose,
  onRemoveInfluence,
  onDeleteNote,
  viewerUserId,
}) => {
  const notesForRender = edge.notes && edge.notes.length > 0
    ? edge.notes
    : (edge.note ? [edge.note] : []);

  return (
    <aside className="pointer-events-auto absolute right-4 top-4 z-20 flex w-full max-w-sm flex-col gap-3 rounded-2xl border border-eoe-border-main bg-eoe-bg-modal/95 p-4 shadow-lg backdrop-blur dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-modal/95">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-eoe-text-sub dark:text-eoe-dark-text-sub">
            {graphMessage(locale, "edgeInspectorTitle")}
          </p>
          <h2 className="mt-1 text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
            {`${edge.sourceName} -> ${edge.targetName}`}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {edge.isNew ? (
            <span className="rounded-full bg-purple-500/15 px-2 py-0.5 text-[11px] font-semibold text-purple-700 dark:text-purple-300">
              {graphMessage(locale, "edgeNewBadge")}
            </span>
          ) : null}
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 text-eoe-text-sub transition-colors hover:bg-eoe-icon hover:text-eoe-text dark:text-eoe-dark-text-sub dark:hover:bg-eoe-dark-icon dark:hover:text-eoe-dark-text"
            aria-label={graphMessage(locale, "closeEdgeInspector")}
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>
      </div>

      {notesForRender.length > 0 ? (
        <div className="flex max-h-72 flex-col gap-2 overflow-y-auto pr-1">
          {notesForRender.map((note) => (
            <NoteRow
              key={note.id}
              note={note}
              locale={locale}
              canManage={
                Boolean(viewerUserId)
                && Number(viewerUserId) === Number(note.user_id)
              }
              onDelete={onDeleteNote ? () => onDeleteNote(note.id) : undefined}
            />
          ))}
        </div>
      ) : (
        <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
          {graphMessage(locale, "edgeInspectorEmpty")}
        </p>
      )}

      {onRemoveInfluence ? (
        <div className="flex justify-end border-t border-eoe-border-main pt-3 dark:border-eoe-dark-border-main">
          <button
            type="button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onRemoveInfluence();
            }}
            className="inline-flex items-center gap-1 rounded-full border border-warning-red/60 bg-eoe-bg px-3 py-1 text-xs font-semibold text-warning-red transition-colors hover:bg-warning-red/10 dark:border-dark-warning-red/60 dark:bg-eoe-dark-bg dark:text-dark-warning-red dark:hover:bg-dark-warning-red/10"
          >
            <Trash2 className="size-3" aria-hidden />
            <span>{graphMessage(locale, "removeInfluenceCta")}</span>
          </button>
        </div>
      ) : null}
    </aside>
  );
};

/**
 * Blank-graph creation and loaded graph collection editing surface.
 */
const GraphExplorerEdit: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser, awaitingUser } = useUser();
  const viewerUserId = currentUser?.id ? Number(currentUser.id) : null;
  const { ensureIssue } = useIssueFromSuggestion();
  const graphLocale = useMemo(
    () => resolveGraphLocale(typeof navigator !== "undefined" ? navigator.language : undefined),
    []
  );
  const collectionId = useMemo(
    () => parsePositiveInt(searchParams.get("collectionId")),
    [searchParams]
  );

  const [selectedIssue, setSelectedIssue] = useState<Suggestion | null>(null);
  const [canvasIssueToAdd, setCanvasIssueToAdd] = useState<Suggestion | null>(null);
  const [rebrewSourceId, setRebrewSourceId] = useState("");
  const [rebrewMode, setRebrewMode] = useState<"copy" | "sync">("copy");
  const [rebrewBusy, setRebrewBusy] = useState(false);
  const [isStartingDraft, setIsStartingDraft] = useState(false);
  const [isAddingCanvasIssue, setIsAddingCanvasIssue] = useState(false);
  const [headerTitleValue, setHeaderTitleValue] = useState("");
  const [isTitleFocused, setIsTitleFocused] = useState(false);
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  /**
   * Legacy state preserved for reset flows that previously controlled the
   * now-retired Details Modal. Kept as a local setter so the rest of the
   * reset/cleanup pipeline doesn't need to change; new UI uses
   * `detailsPanelOpen` below instead.
   */
  const [, setDetailsOpen] = useState(false);
  /**
   * Tracks whether the inline details form has unsaved edits. We only consume
   * the setter (pipeline + CollectionPostForm's `onDirtyChange`); the boolean
   * itself isn't read directly anymore since the save/discard flow now goes
   * through the bottom action bar and the unsaved-changes dialog.
   */
  const [, setDetailsDirty] = useState(false);
  const [detailsCloseConfirmOpen, setDetailsCloseConfirmOpen] = useState(false);

  const [isSnapshotLoading, setIsSnapshotLoading] = useState(false);
  const [snapshotError, setSnapshotError] = useState<string | null>(null);
  const [loadedCollection, setLoadedCollection] = useState<GraphCollectionSnapshot["collection"]>(null);
  const [nodes, setNodes] = useState<Array<Node<GraphIssueNodeData>>>([]);
  const [edges, setEdges] = useState<Array<Edge<GraphCanvasEdgeData>>>([]);
  const [sources, setSources] = useState<GraphSourceDescriptor[]>([]);
  const [itemRelations, setItemRelations] = useState<
    NonNullable<GraphCollectionSnapshot["item_relations"]>
  >([]);
  /**
   * Controls the inline DetailPanel (mobile + desktop). We intentionally use a
   * collapsible panel instead of a secondary Modal so the editor feels like
   * one continuous surface rather than a stack of layers — easier to reason
   * about z-indexes and keyboard flow, and nicer on mobile where a modal over
   * a full-screen editor is confusing.
   */
  const [detailsPanelOpen, setDetailsPanelOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  /**
   * Viewport-based mobile flag driving graph node layout. Intentionally
   * viewport-based, not `userIsMobile` from UserContext, so the editor
   * responds to narrow-window resizes and dev-tools mobile emulation.
   */
  const isMobile = useMobileViewport();
  const [addInfluenceOpen, setAddInfluenceOpen] = useState(false);
  const [addInfluenceDirection, setAddInfluenceDirection] = useState<"influencer" | "influenced">("influencer");
  const [addInfluenceCenterIssue, setAddInfluenceCenterIssue] = useState<Issue | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [stickyComposerEdgeId, setStickyComposerEdgeId] = useState<string | null>(null);
  const [newEdgeIds, setNewEdgeIds] = useState<Set<string>>(new Set());
  const [edgeComposerMediaFiles, setEdgeComposerMediaFiles] = useState<FileList | null>(null);
  const [isEdgeNoteSubmitting, setIsEdgeNoteSubmitting] = useState(false);
  /**
   * When open, the AI graph analysis panel is mounted on top of the
   * canvas (same slot as `GraphEdgeInspector`). Gated behind the
   * `ai_graph_analysis` experimental feature flag so we can ramp rollout.
   */
  const [isAnalysisOpen, setIsAnalysisOpen] = useState(false);
  const analysisFeatureFlag = useFeatureFlag("ai_graph_analysis");
  const isAnalysisEnabled = analysisFeatureFlag.isEnabled;
  const analyzeGraphCopy = useMemo(
    () => getAiUiStrings(resolveAiLocale()).analyzeGraph,
    []
  );

  const pendingPositionSavesRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const positionSaveTimerRef = useRef<number | null>(null);
  const activeSnapshotRequestRef = useRef(0);
  const newEdgeTimersRef = useRef<Map<string, number>>(new Map());
  const edgesRef = useRef<Array<Edge<GraphCanvasEdgeData>>>([]);
  const detailsFormRef = useRef<CollectionFormHandle>(null);

  useEffect(() => () => {
    newEdgeTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    newEdgeTimersRef.current.clear();
  }, []);

  useEffect(() => {
    edgesRef.current = edges;
  }, [edges]);

  const openAddInfluence = useCallback(
    (issue: Issue, direction: "influencer" | "influenced") => {
      setAddInfluenceCenterIssue(issue);
      setAddInfluenceDirection(direction);
      setAddInfluenceOpen(true);
    },
    []
  );

  const clearNewEdgeMarkers = useCallback(() => {
    newEdgeTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    newEdgeTimersRef.current.clear();
    setNewEdgeIds(new Set());
  }, []);

  const markNewEdge = useCallback((edgeId: string) => {
    setNewEdgeIds((current) => {
      const next = new Set(current);
      next.add(edgeId);
      return next;
    });

    const existingTimer = newEdgeTimersRef.current.get(edgeId);
    if (typeof existingTimer === "number") {
      window.clearTimeout(existingTimer);
    }
    const timerId = window.setTimeout(() => {
      newEdgeTimersRef.current.delete(edgeId);
      setNewEdgeIds((current) => {
        if (!current.has(edgeId)) {
          return current;
        }
        const next = new Set(current);
        next.delete(edgeId);
        return next;
      });
    }, NEW_EDGE_HIGHLIGHT_MS);
    newEdgeTimersRef.current.set(edgeId, timerId);
  }, []);

  const dismissStickyComposer = useCallback(() => {
    setStickyComposerEdgeId(null);
    setEdgeComposerMediaFiles(null);
  }, []);

  useEffect(() => {
    clearNewEdgeMarkers();
    setSelectedEdgeId(null);
    setStickyComposerEdgeId(null);
    setEdgeComposerMediaFiles(null);
    setIsEdgeNoteSubmitting(false);
    setCanvasIssueToAdd(null);
    setIsStartingDraft(false);
    setIsAddingCanvasIssue(false);
    setHeaderTitleValue("");
    setIsTitleFocused(false);
    setIsSavingTitle(false);
    setDetailsOpen(false);
    setDetailsPanelOpen(false);
    setDetailsDirty(false);
    setDetailsCloseConfirmOpen(false);
    setDeleteConfirmOpen(false);
  }, [clearNewEdgeMarkers, collectionId]);

  const getConnectedComponentIds = useCallback(
    (nodeId: string): Set<string> => {
      const component = new Set<string>([nodeId]);
      const queue = [nodeId];
      const adjacency = new Map<string, Set<string>>();
      edgesRef.current.forEach((edge) => {
        if (!adjacency.has(edge.source)) {
          adjacency.set(edge.source, new Set());
        }
        adjacency.get(edge.source)?.add(edge.target);
        if (!adjacency.has(edge.target)) {
          adjacency.set(edge.target, new Set());
        }
        adjacency.get(edge.target)?.add(edge.source);
      });

      while (queue.length > 0) {
        const currentId = queue.shift();
        if (!currentId) {
          continue;
        }
        (adjacency.get(currentId) || new Set()).forEach((nextId) => {
          if (!component.has(nextId)) {
            component.add(nextId);
            queue.push(nextId);
          }
        });
      }

      return component;
    },
    []
  );

  const flushPositionSaves = useCallback(async () => {
    if (!collectionId) {
      pendingPositionSavesRef.current.clear();
      return;
    }

    const pendingEntries = Array.from(pendingPositionSavesRef.current.entries());
    pendingPositionSavesRef.current.clear();

    if (positionSaveTimerRef.current !== null) {
      window.clearTimeout(positionSaveTimerRef.current);
      positionSaveTimerRef.current = null;
    }

    if (pendingEntries.length === 0) {
      return;
    }

    const results = await Promise.allSettled(
      pendingEntries.map(([issueId, position]) =>
        collectionApi.patchItemPosition(collectionId, "issue", issueId, position)
      )
    );

    if (results.some((result) => result.status === "rejected")) {
      toast.error(graphMessage(graphLocale, "positionSaveError"));
    }
  }, [collectionId, graphLocale]);

  const queuePositionSave = useCallback(
    (issueId: number, position: { x: number; y: number }) => {
      if (!collectionId || !Number.isFinite(issueId) || issueId <= 0) {
        return;
      }
      pendingPositionSavesRef.current.set(issueId, position);
      if (positionSaveTimerRef.current !== null) {
        window.clearTimeout(positionSaveTimerRef.current);
      }
      positionSaveTimerRef.current = window.setTimeout(() => {
        void flushPositionSaves();
      }, POSITION_SAVE_DEBOUNCE_MS);
    },
    [collectionId, flushPositionSaves]
  );

  useEffect(() => {
    const pendingPositionSaves = pendingPositionSavesRef.current;
    return () => {
      if (positionSaveTimerRef.current !== null) {
        window.clearTimeout(positionSaveTimerRef.current);
      }
      pendingPositionSaves.clear();
    };
  }, []);

  useEffect(() => {
    if (positionSaveTimerRef.current !== null) {
      window.clearTimeout(positionSaveTimerRef.current);
      positionSaveTimerRef.current = null;
    }
    pendingPositionSavesRef.current.clear();
  }, [collectionId]);

  const moveConnectedGroup = useCallback(
    (nodeId: string, dx: number, dy: number) => {
      const componentIds = getConnectedComponentIds(nodeId);
      const movedNodes: Array<{ issueId: number; position: { x: number; y: number } }> = [];

      setNodes((current) =>
        current.map((node) => {
          if (!componentIds.has(node.id)) {
            return node;
          }
          const nextPosition = {
            x: node.position.x + dx,
            y: node.position.y + dy,
          };
          const issueId = Number(node.id);
          if (Number.isFinite(issueId) && issueId > 0) {
            movedNodes.push({ issueId, position: nextPosition });
          }
          return {
            ...node,
            position: nextPosition,
          };
        })
      );

      movedNodes.forEach(({ issueId, position }) => {
        queuePositionSave(issueId, position);
      });
    },
    [getConnectedComponentIds, queuePositionSave]
  );

  /**
   * Indirection ref for the node-level "remove from graph" handler. The
   * handler itself needs `loadCollectionGraph` (declared below), but
   * `decorateNodes` needs to be able to build node data with an onRemove
   * callback before that. We populate this ref in an effect so the handler
   * is always wired up without triggering re-decoration loops.
   */
  const removeIssueFromCollectionRef = useRef<(node: Node<GraphIssueNodeData>) => Promise<void>>(
    async () => undefined
  );

  /**
   * Ref view of the remix source attribution map so `decorateNodes` can read
   * from the latest value without taking a dependency that would force a
   * rebuild on every snapshot change. `decorateNodes` is memoized on coarse
   * inputs (locale, isMobile, etc.) to keep node identity stable under drags.
   * The map itself is computed further down and synced into the ref via an
   * effect; this forward declaration just pins the ref slot.
   */
  const issueSourceStylesRef = useRef<
    Map<string, { outlineColors: string[]; isOverlap: boolean }>
  >(new Map());

  const decorateNodes = useCallback(
    (
      baseNodes: Array<Node<GraphIssueNodeData>>,
      edgeList: Array<Edge<GraphCanvasEdgeData>> = edgesRef.current
    ): Array<Node<GraphIssueNodeData>> => {
      const edgeCounts = computeEdgeCounts(edgeList);
      return baseNodes.map((node) => {
        const counts = edgeCounts[node.id] || { inCount: 0, outCount: 0 };
        const sourceStyle = issueSourceStylesRef.current.get(node.id) ?? null;
        return {
        ...node,
        data: {
          ...node.data,
          additionalInfluencersCount: 0,
          additionalInfluencedCount: 0,
            totalInfluencers: counts.inCount,
            totalInfluenced: counts.outCount,
            onExpandInfluencers: () => undefined,
            onExpandInfluenced: () => undefined,
            onAddInfluencer: () => openAddInfluence(node.data.entity, "influencer"),
            onAddInfluenced: () => openAddInfluence(node.data.entity, "influenced"),
            onGroupDragDelta: (dx: number, dy: number) => moveConnectedGroup(node.id, dx, dy),
            onRemoveFromCollection: collectionId
              ? () => {
                  void removeIssueFromCollectionRef.current(node);
                }
              : undefined,
            editable: Boolean(collectionId),
            sourceStyle,
            isMobile,
            locale: graphLocale,
          },
        };
      });
    },
    [collectionId, graphLocale, isMobile, moveConnectedGroup, openAddInfluence]
  );

  // `issueSourceStyles` and the ref-sync effect are declared further down
  // (needs `sources`/`itemRelations` state).

  const loadCollectionGraph = useCallback(
    async (targetCollectionId: number) => {
      const requestId = activeSnapshotRequestRef.current + 1;
      activeSnapshotRequestRef.current = requestId;
      setIsSnapshotLoading(true);
      setSnapshotError(null);

      try {
        const snapshot = await graphCollectionsApi.getSnapshot(targetCollectionId);
        if (activeSnapshotRequestRef.current !== requestId) {
          return null;
        }
        const canvasData = mapGraphSnapshotToCanvasData(snapshot);
        setLoadedCollection(snapshot.collection ?? null);
        setEdges(canvasData.edges);
        setNodes(decorateNodes(canvasData.nodes, canvasData.edges));
        setSources(snapshot.sources ?? []);
        setItemRelations(snapshot.item_relations ?? []);
        return canvasData;
      } catch (error) {
        if (activeSnapshotRequestRef.current !== requestId) {
          return null;
        }
        console.error("Failed to load graph collection snapshot:", error);
        setLoadedCollection(null);
        setEdges([]);
        setNodes([]);
        setSources([]);
        setItemRelations([]);
        setSnapshotError(graphMessage(graphLocale, "loadGraphError"));
        return null;
      } finally {
        if (activeSnapshotRequestRef.current === requestId) {
          setIsSnapshotLoading(false);
        }
      }
    },
    [decorateNodes, graphLocale]
  );

  useEffect(() => {
    if (!collectionId) {
      setLoadedCollection(null);
      setSnapshotError(null);
      setIsSnapshotLoading(false);
      setEdges([]);
      setNodes([]);
      return;
    }
    // Wait for the session resolver (UserContext.fetchUser) to settle before
    // asking for the snapshot. Direct-URL loads otherwise fire the GET before
    // axios has the signed-in cookie attached, so a private/me collection
    // returns 404 and we render "Unable to load this graph collection right
    // now" even though the user is authenticated. After auth resolves the
    // effect re-runs with the proper viewer id.
    if (awaitingUser) {
      setIsSnapshotLoading(true);
      return;
    }
    void loadCollectionGraph(collectionId);
  }, [collectionId, loadCollectionGraph, awaitingUser]);

  /**
   * Populate the node-removal handler now that `loadCollectionGraph` exists.
   * Compound issues go through `detachInfluence` so the caller's
   * collection-scoped edge notes are also soft-deleted; non-compound issues
   * use the simpler `removeIssue` endpoint. Both flows fire a Sonner Undo
   * toast that reverses the mutation via the matching restore endpoint.
   */
  useEffect(() => {
    removeIssueFromCollectionRef.current = async (issueNode) => {
      if (!collectionId) {
        return;
      }
      const attrs = issueNode.data.entity as Issue & {
        subject_id?: number | string | null;
        topic_id?: number | string | null;
      };
      const isCompound = Number(attrs.subject_id) > 0 && Number(attrs.topic_id) > 0;
      const issueId = Number(issueNode.data.entity.id);
      if (!Number.isFinite(issueId) || issueId <= 0) {
        return;
      }
      try {
        if (isCompound) {
          const response = await graphCollectionsApi.detachInfluence(collectionId, issueId);
          await loadCollectionGraph(collectionId);
          toastUndo({
            message: graphMessage(graphLocale, "removeInfluenceToast"),
            undoLabel: graphMessage(graphLocale, "undo"),
            onUndo: async () => {
              await graphCollectionsApi.restoreInfluence(collectionId, issueId, {
                deactivatedCommentIds: response.deactivatedCommentIds || [],
              });
              await loadCollectionGraph(collectionId);
            },
            undoSuccessMessage: graphMessage(graphLocale, "restoreSucceededToast"),
            undoErrorMessage: graphMessage(graphLocale, "restoreFailedToast"),
          });
        } else {
          await graphCollectionsApi.removeIssue(collectionId, issueId);
          const savedPosition = issueNode.position ?? null;
          await loadCollectionGraph(collectionId);
          toastUndo({
            message: graphMessage(graphLocale, "removeIssueToast"),
            undoLabel: graphMessage(graphLocale, "undo"),
            onUndo: async () => {
              await graphCollectionsApi.restoreIssue(collectionId, issueId, {
                position: savedPosition,
              });
              await loadCollectionGraph(collectionId);
            },
            undoSuccessMessage: graphMessage(graphLocale, "restoreSucceededToast"),
            undoErrorMessage: graphMessage(graphLocale, "restoreFailedToast"),
          });
        }
      } catch (error) {
        console.error("Failed to remove node from collection:", error);
        toast.error(
          graphMessage(
            graphLocale,
            isCompound ? "removeInfluenceError" : "removeIssueError"
          )
        );
      }
    };
  }, [collectionId, graphLocale, loadCollectionGraph]);

  useEffect(() => {
    if (nodes.length === 0) {
      return;
    }
    setNodes((current) => decorateNodes(current));
  }, [decorateNodes, nodes.length]);

  const edgeNodeNameById = useMemo(
    () =>
      new Map(
        nodes.map((node) => [
          node.id,
          node.data.entity.name ||
            node.data.entity.cname?.replace(/_/g, " ") ||
            graphMessage(graphLocale, "unknownIssue"),
        ])
      ),
    [graphLocale, nodes]
  );

  /**
   * Stable palette used for remix source outlines. Colors are chosen so they
   * read clearly against both light and dark canvas backgrounds without
   * overlapping the new-edge purple. The index wraps if a remix somehow has
   * more than the palette length; this is acceptable because it keeps the
   * UI from crashing on edge cases rather than hard-limiting the remix.
   */
  const SOURCE_PALETTE = useMemo(
    () => [
      "#2563eb", // blue-600
      "#ea580c", // orange-600
      "#16a34a", // green-600
      "#db2777", // pink-600
      "#0891b2", // cyan-600
      "#9333ea", // purple-600
      "#ca8a04", // amber-600
    ],
    []
  );

  const sourceColorById = useMemo(() => {
    const map = new Map<number, string>();
    sources
      .filter((source) => source.role === "source")
      .forEach((source, idx) => {
        map.set(
          source.collectionId,
          SOURCE_PALETTE[idx % SOURCE_PALETTE.length]
        );
      });
    return map;
  }, [SOURCE_PALETTE, sources]);

  const isRemix = sources.filter((source) => source.role === "source").length >= 2;

  /**
   * Per-issue mapping of source collection ids the issue came from. Uses
   * `item_relations[].merged_from` when available (which is a string tag
   * like "local" or "sync"); because the snapshot doesn't yet carry a
   * per-source attribution on each item, we fall back to painting every
   * merged-from-sync node with a single accent color. This is a safe
   * visual approximation that makes the remix outlines readable without
   * claiming more attribution than we actually know.
   */
  const issueSourceStyles = useMemo(() => {
    if (!isRemix) {
      return new Map<string, { outlineColors: string[]; isOverlap: boolean }>();
    }
    const mergedBySource = Array.from(sourceColorById.values());
    if (mergedBySource.length === 0) {
      return new Map<string, { outlineColors: string[]; isOverlap: boolean }>();
    }
    const result = new Map<string, { outlineColors: string[]; isOverlap: boolean }>();
    itemRelations.forEach((rel) => {
      const id = String(rel.related_id);
      if (!id) {
        return;
      }
      const mergedFrom = typeof rel.merged_from === "string"
        ? rel.merged_from.toLowerCase()
        : "";
      if (mergedFrom === "local") {
        return;
      }
      // Sync / unknown origin → paint with the first two source colors so
      // overlapping remix nodes appear banded. This is intentionally
      // approximate; finer attribution would require per-source item lists.
      const existing = result.get(id);
      if (!existing) {
        result.set(id, {
          outlineColors: [mergedBySource[0]],
          isOverlap: false,
        });
        return;
      }
      if (existing.outlineColors.length < 2 && mergedBySource.length >= 2) {
        result.set(id, {
          outlineColors: [...existing.outlineColors, mergedBySource[1]],
          isOverlap: true,
        });
      }
    });
    return result;
  }, [isRemix, itemRelations, sourceColorById]);

  /**
   * Sync the computed source attribution map into a ref that `decorateNodes`
   * reads from. Kept as a separate effect so the memoized `decorateNodes`
   * callback stays stable under drag/drop; node re-decoration driven by
   * attribution changes happens in the paired effect below.
   */
  useEffect(() => {
    issueSourceStylesRef.current = issueSourceStyles;
    setNodes((current) => {
      if (current.length === 0) {
        return current;
      }
      return decorateNodes(current);
    });
  }, [decorateNodes, issueSourceStyles]);

  const collectionProperties = useMemo<Record<string, unknown>>(() => {
    const rawProperties = loadedCollection?.properties;
    return rawProperties && typeof rawProperties === "object"
      ? (rawProperties as Record<string, unknown>)
      : {};
  }, [loadedCollection?.properties]);

  /**
   * Numeric seed issue id stored on the collection. Used both for the
   * auto-generated title and to drive the Recenter control so the viewport
   * returns to the user's starting issue rather than an arbitrary origin.
   */
  const seedIssueId = useMemo(
    () => parsePositiveInt(String(collectionProperties.seed_issue_id ?? "")),
    [collectionProperties.seed_issue_id]
  );

  const rootNodeId = useMemo<string | null>(
    () => (seedIssueId ? String(seedIssueId) : null),
    [seedIssueId]
  );

  const autoGeneratedTitle = useMemo(() => {
    if (!seedIssueId) {
      return null;
    }
    const seedNode = nodes.find((node) => Number(node.id) === seedIssueId);
    const seedLabel =
      seedNode?.data.entity.name ||
      seedNode?.data.entity.cname?.replace(/_/g, " ") ||
      null;
    return seedLabel ? `Graph · ${seedLabel}` : null;
  }, [nodes, seedIssueId]);

  const isUsingAutoGeneratedTitle = useMemo(() => {
    const currentTitle =
      typeof loadedCollection?.title === "string" ? loadedCollection.title.trim() : "";
    const normalizedAutoTitle = autoGeneratedTitle ? autoGeneratedTitle.trim() : "";
    return currentTitle.length > 0 && normalizedAutoTitle.length > 0 && currentTitle === normalizedAutoTitle;
  }, [autoGeneratedTitle, loadedCollection?.title]);

  useEffect(() => {
    if (isTitleFocused) {
      return;
    }

    const currentTitle =
      typeof loadedCollection?.title === "string" ? loadedCollection.title : "";
    setHeaderTitleValue(isUsingAutoGeneratedTitle ? "" : currentTitle);
  }, [isTitleFocused, isUsingAutoGeneratedTitle, loadedCollection?.title]);

  const selectedEdge = useMemo<GraphSelectedEdge | null>(() => {
    if (!selectedEdgeId) {
      return null;
    }
    const edge = edges.find((candidate) => candidate.id === selectedEdgeId);
    if (!edge) {
      return null;
    }

    return {
      id: edge.id,
      relationId:
        edge.data?.relationId != null && Number.isFinite(Number(edge.data.relationId))
          ? Number(edge.data.relationId)
          : null,
      sourceName: edgeNodeNameById.get(edge.source) || edge.source,
      targetName: edgeNodeNameById.get(edge.target) || edge.target,
      note: edge.data?.note ?? null,
      notes: Array.isArray(edge.data?.notes) ? edge.data.notes : [],
      isNew: newEdgeIds.has(edge.id),
    };
  }, [edgeNodeNameById, edges, newEdgeIds, selectedEdgeId]);

  const stickyComposerEdge = useMemo(
    () => edges.find((edge) => edge.id === stickyComposerEdgeId) ?? null,
    [edges, stickyComposerEdgeId]
  );

  const {
    state: edgeSelectionStateValue,
    selectionByEdgeId,
    focusedEdgeId,
    handleEdgeClick: handleEdgeSelectionClick,
    clear: clearEdgeSelection,
  } = useEdgeSelection(edges);

  /*
   * Editor edge decoration. All visual styling (stroke color / width / glow
   * / animation) lives inside the `GraphInfluenceEdge` custom edge so every
   * graph surface — editor, feed, explore, compact picker — stays
   * pixel-consistent. This memo only threads the per-edge selection state
   * and newness flag onto `edge.data`; the custom edge reads those to pick
   * the right purple shade and animation.
   */
  const decoratedEdges = useMemo(
    () =>
      edges.map((edge) => {
        const isNew = newEdgeIds.has(edge.id);
        const selectionState = selectionByEdgeId.get(edge.id) ?? "none";
        const isFocused =
          edge.id === selectedEdgeId
          || edge.id === focusedEdgeId
          || selectionState === "focus";
        return {
          ...edge,
          animated: isNew || isFocused || Boolean(edge.animated),
          selected: isFocused,
          data: {
            ...(edge.data || {}),
            isNew,
            selectionState,
            locale: graphLocale,
          },
        };
      }),
    [edges, focusedEdgeId, graphLocale, newEdgeIds, selectedEdgeId, selectionByEdgeId]
  );

  useEffect(() => {
    if (selectedEdgeId && !edges.some((edge) => edge.id === selectedEdgeId)) {
      setSelectedEdgeId(null);
    }
    if (stickyComposerEdgeId && !edges.some((edge) => edge.id === stickyComposerEdgeId)) {
      setStickyComposerEdgeId(null);
      setEdgeComposerMediaFiles(null);
    }
  }, [edges, selectedEdgeId, stickyComposerEdgeId]);

  // Keep the "focused edge" inspector synced to the two-step selection state.
  // `focusedEdgeId` transitions come from the reducer; we mirror into the
  // legacy `selectedEdgeId` slot so downstream inspector code keeps working.
  useEffect(() => {
    setSelectedEdgeId((current) => (current === focusedEdgeId ? current : focusedEdgeId));
  }, [focusedEdgeId]);

  /**
   * Live-region announcement for assistive tech as the two-step selection
   * state changes. Kept separate from the toasts so screen-reader users do
   * not get double-read by Sonner's own announcement heuristics.
   */
  const liveAnnouncement = useMemo(() => {
    if (edgeSelectionStateValue.kind === "none") {
      return graphMessage(graphLocale, "liveRegionCleared");
    }
    if (edgeSelectionStateValue.kind === "focus") {
      return graphMessage(graphLocale, "liveRegionFocus");
    }
    return formatGraphMessage(graphLocale, "liveRegionNeighborhood", {
      count: edgeSelectionStateValue.edgeIds.size,
    });
  }, [edgeSelectionStateValue, graphLocale]);

  const handleEdgeClick: EdgeMouseHandler = useCallback(
    (_event, edge) => {
      const graphEdge = edge as Edge<GraphCanvasEdgeData>;
      handleEdgeSelectionClick(graphEdge);
      if (newEdgeIds.has(graphEdge.id) && !graphEdge.data?.note) {
        setStickyComposerEdgeId(graphEdge.id);
      } else {
        setStickyComposerEdgeId(null);
        setEdgeComposerMediaFiles(null);
      }
    },
    [handleEdgeSelectionClick, newEdgeIds]
  );

  const handleStartFromIssue = useCallback(async () => {
    if (!selectedIssue || isStartingDraft) {
      return;
    }

    setIsStartingDraft(true);
    try {
      const seedIssue = await ensureIssue(selectedIssue);
      if (!seedIssue?.id) {
        throw new Error("GRAPH_SEED_MISSING");
      }

      const createdCollection = await createCollection({
        issueId: null,
        title: `Graph · ${getGraphSeedLabel(seedIssue, graphLocale)}`,
        body: null,
        url: null,
        audience: "public",
        status: "draft",
        properties: {
          privacy: "public",
          collection_type: "graph",
          graph_mode: "blank",
          seed_issue_id: Number(seedIssue.id),
        },
      });

      await collectionApi.addItem(createdCollection.id, {
        entityId: Number(seedIssue.id),
        entityType: "issue",
        properties: {
          position: INITIAL_SEED_POSITION,
        },
      });

      setSelectedIssue(null);
      navigate(`/graph?mode=edit&collectionId=${encodeURIComponent(String(createdCollection.id))}`);
      toast.success(graphMessage(graphLocale, "draftGraphCreatedToast"));
    } catch (error) {
      console.error("Failed to start graph draft from issue:", error);
      toast.error(graphMessage(graphLocale, "seedAttachError"));
    } finally {
      setIsStartingDraft(false);
    }
  }, [ensureIssue, graphLocale, isStartingDraft, navigate, selectedIssue]);

  const handleHeaderTitleBlur = useCallback(async () => {
    setIsTitleFocused(false);
    if (!collectionId || !loadedCollection) {
      return;
    }

    const trimmedTitle = headerTitleValue.trim();
    // Draft collections are allowed to have a blank title — only Publish
    // requires one. If the input is empty AND we're still a draft, persist
    // the null so the user's intent (no title yet) is preserved. For
    // published collections, revert to the previous title so an accidental
    // blur doesn't erase the published title.
    const isDraft = loadedCollection.status === "draft";
    if (!trimmedTitle) {
      if (!isDraft) {
        setHeaderTitleValue(isUsingAutoGeneratedTitle ? "" : (loadedCollection.title || ""));
        return;
      }
      if (!loadedCollection.title || loadedCollection.title === "") {
        return;
      }
    }
    if (trimmedTitle === (loadedCollection.title || "") || isSavingTitle) {
      return;
    }

    setIsSavingTitle(true);
    try {
      const response = await api.patch(apiRoutes.posts.byId(collectionId), {
        title: trimmedTitle.length > 0 ? trimmedTitle : null,
      });
      setLoadedCollection(response.data as GraphCollectionSnapshot["collection"]);
      setHeaderTitleValue(trimmedTitle);
    } catch (error) {
      console.error("Failed to save graph title:", error);
      setHeaderTitleValue(isUsingAutoGeneratedTitle ? "" : (loadedCollection.title || ""));
      toast.error(graphMessage(graphLocale, "titleSaveError"));
    } finally {
      setIsSavingTitle(false);
    }
  }, [
    collectionId,
    graphLocale,
    headerTitleValue,
    isSavingTitle,
    isUsingAutoGeneratedTitle,
    loadedCollection,
  ]);

  const handleDetailsSaveAndClose = useCallback(() => {
    setDetailsCloseConfirmOpen(false);
    detailsFormRef.current?.submit();
  }, []);

  const handleDetailsDiscard = useCallback(() => {
    setDetailsCloseConfirmOpen(false);
    setDetailsDirty(false);
    setDetailsOpen(false);
  }, []);

  const handleDetailsSaved = useCallback((collection: CollectionPostResponse) => {
    setLoadedCollection(collection as GraphCollectionSnapshot["collection"]);
    setDetailsDirty(false);
    setDetailsCloseConfirmOpen(false);
    setDetailsOpen(false);
  }, []);

  /**
   * Draft → Published transition. The backend flips `status` to `published`
   * and stamps `published_at`. We PATCH the post directly via apiRoutes.posts
   * for compatibility with the existing CollectionPostForm save path.
   */
  const handlePublish = useCallback(async (): Promise<void> => {
    if (!collectionId || isPublishing) return;
    setIsPublishing(true);
    try {
      const response = await api.patch<CollectionPostResponse>(
        apiRoutes.posts.byId(collectionId),
        { status: "published" }
      );
      setLoadedCollection(response.data as GraphCollectionSnapshot["collection"]);
      toast.success(graphMessage(graphLocale, "publishedToast"));
    } catch (error) {
      console.error("Failed to publish graph:", error);
      toast.error(graphMessage(graphLocale, "publishError"));
    } finally {
      setIsPublishing(false);
    }
  }, [collectionId, graphLocale, isPublishing]);

  /**
   * Delete the current graph collection. Navigation returns the user to the
   * graph explorer root after a successful delete; errors surface as a toast
   * and leave the editor untouched so no work is lost.
   */
  const handleDelete = useCallback(async (): Promise<void> => {
    if (!collectionId || isDeleting) return;
    setIsDeleting(true);
    try {
      await api.delete(apiRoutes.posts.byId(collectionId));
      setDeleteConfirmOpen(false);
      toast.success(graphMessage(graphLocale, "delete"));
      navigate("/graph");
    } catch (error) {
      console.error("Failed to delete graph collection:", error);
      toast.error(graphMessage(graphLocale, "loadGraphError"));
      setIsDeleting(false);
    }
  }, [collectionId, graphLocale, isDeleting, navigate]);

  /**
   * Explicit "Save Draft" action — equivalent to a title/detail save while the
   * collection is still in draft status. We pipe through the details form ref
   * so its full validation and media-upload flow runs; if the details panel is
   * closed we short-circuit to just re-save the title (already debounced on
   * blur, this is the explicit confirmation handler for the action bar).
   */
  const handleSaveDraft = useCallback(async (): Promise<void> => {
    if (!collectionId || isSavingDraft) return;
    setIsSavingDraft(true);
    try {
      if (detailsFormRef.current) {
        detailsFormRef.current.submit();
      }
      toast.success(graphMessage(graphLocale, "draftSavedToast"));
    } catch (error) {
      console.error("Failed to save draft:", error);
      toast.error(graphMessage(graphLocale, "titleSaveError"));
    } finally {
      setIsSavingDraft(false);
    }
  }, [collectionId, graphLocale, isSavingDraft]);

  const handleRebrew = useCallback(async () => {
    const sourceCollectionId = Number.parseInt(String(rebrewSourceId).trim(), 10);
    if (!Number.isFinite(sourceCollectionId) || sourceCollectionId <= 0) {
      toast.error(graphMessage(graphLocale, "rebrewSourceInvalid"));
      return;
    }

    setRebrewBusy(true);
    try {
      const result = await graphCollectionsApi.rebrew({
        sourceCollectionId,
        mode: rebrewMode,
      });
      const createdCollectionId = parsePositiveInt(
        String((result.collection as { id?: number | string }).id ?? "")
      );
      toast.success(graphMessage(graphLocale, "collectionCreatedToast"));
      if (createdCollectionId) {
        navigate(`/graph?mode=edit&collectionId=${encodeURIComponent(String(createdCollectionId))}`);
      } else {
        navigate("/graph");
      }
    } catch (error) {
      console.error("Failed to rebrew graph collection:", error);
      toast.error(graphMessage(graphLocale, "rebrewError"));
    } finally {
      setRebrewBusy(false);
    }
  }, [graphLocale, navigate, rebrewMode, rebrewSourceId]);

  const handleAddLooseIssue = useCallback(async () => {
    if (!collectionId || !canvasIssueToAdd || isAddingCanvasIssue) {
      return;
    }

    setIsAddingCanvasIssue(true);
    try {
      const issue = await ensureIssue(canvasIssueToAdd);
      if (!issue?.id) {
        throw new Error("GRAPH_ISSUE_ADD_INVALID");
      }

      if (nodes.some((node) => node.id === String(issue.id))) {
        setCanvasIssueToAdd(null);
        toast.success(graphMessage(graphLocale, "issueAlreadyInGraph"));
        return;
      }

      const addedIssueId = Number(issue.id);
      await collectionApi.addItem(collectionId, {
        entityId: addedIssueId,
        entityType: "issue",
        properties: {
          position: getNextLooseIssuePosition(nodes),
        },
      });

      await loadCollectionGraph(collectionId);
      setCanvasIssueToAdd(null);
      toastSuccessWithUndo({
        message: graphMessage(graphLocale, "issueAddedToGraph"),
        undoLabel: graphMessage(graphLocale, "undo"),
        onUndo: async () => {
          await graphCollectionsApi.removeIssue(collectionId, addedIssueId);
          await loadCollectionGraph(collectionId);
        },
        undoSuccessMessage: graphMessage(graphLocale, "restoreSucceededToast"),
        undoErrorMessage: graphMessage(graphLocale, "restoreFailedToast"),
      });
    } catch (error) {
      console.error("Failed to add standalone issue to graph:", error);
      toast.error(graphMessage(graphLocale, "loadGraphError"));
    } finally {
      setIsAddingCanvasIssue(false);
    }
  }, [canvasIssueToAdd, collectionId, ensureIssue, graphLocale, isAddingCanvasIssue, loadCollectionGraph, nodes]);

  const handleInfluenceAdded = useCallback(
    async (newIssue: Issue) => {
      if (!collectionId || !addInfluenceCenterIssue) {
        return;
      }

      const centerNode = nodes.find((node) => node.id === String(addInfluenceCenterIssue.id));
      const centerPosition = centerNode?.position || INITIAL_SEED_POSITION;
      const targetPosition = findFreeInfluencePosition(
        centerPosition,
        addInfluenceDirection,
        nodes
      );

      try {
        // If the issue is already on the canvas (e.g. duplicate-influence fallback),
        // skip the add call but still reload so the new edge shows up.
        const alreadyOnCanvas = nodes.some(
          (node) => node.id === String(newIssue.id)
        );
        if (!alreadyOnCanvas) {
          await collectionApi.addItem(collectionId, {
            entityId: Number(newIssue.id),
            entityType: "issue",
            properties: {
              position: targetPosition,
            },
          });
        }
        const canvasData = await loadCollectionGraph(collectionId);
        const sourceId =
          addInfluenceDirection === "influencer"
            ? Number(newIssue.id)
            : Number(addInfluenceCenterIssue.id);
        const targetId =
          addInfluenceDirection === "influencer"
            ? Number(addInfluenceCenterIssue.id)
            : Number(newIssue.id);
        const createdEdge =
          canvasData?.edges.find(
            (edge) =>
              edge.source === String(sourceId) &&
              edge.target === String(targetId)
          ) ?? null;

        if (createdEdge) {
          markNewEdge(createdEdge.id);
          setSelectedEdgeId(createdEdge.id);
          setStickyComposerEdgeId(createdEdge.id);
          setEdgeComposerMediaFiles(null);
        }
      } catch (error) {
        console.error("Failed to attach influenced issue to collection graph:", error);
        toast.error(graphMessage(graphLocale, "loadGraphError"));
      }
    },
    [
      addInfluenceCenterIssue,
      addInfluenceDirection,
      collectionId,
      graphLocale,
      loadCollectionGraph,
      markNewEdge,
      nodes,
    ]
  );

  const handleAddInfluenceClose = useCallback(() => {
    setAddInfluenceOpen(false);
    setAddInfluenceCenterIssue(null);
  }, []);

  /**
   * React Flow `onConnect`: the user dragged from a source handle to another
   * node's target handle. Post to the graph service which either reuses an
   * existing compound issue for the pair or creates one, and attaches it to
   * the collection. Reload on success, mark the new edge, and fire an Undo
   * toast so the connect is reversible.
   */
  const handleCanvasConnect: OnConnect = useCallback(
    (connection: Connection) => {
      if (!collectionId || !connection.source || !connection.target) {
        return;
      }
      if (connection.source === connection.target) {
        return;
      }
      const influencerIssueId = Number(connection.source);
      const influencedIssueId = Number(connection.target);
      if (!Number.isFinite(influencerIssueId) || !Number.isFinite(influencedIssueId)) {
        return;
      }
      // Optimistically append a "pending" edge so the user sees instant feedback.
      const optimisticEdgeId = `pending-${connection.source}->${connection.target}-${Date.now()}`;
      setEdges((current) => [
        ...current,
        {
          id: optimisticEdgeId,
          source: String(connection.source),
          target: String(connection.target),
          type: "influenceEdge",
          animated: true,
          data: {
            relationId: null,
            note: null,
            notes: [],
            badges: {
              otherCollections: 0,
              notes: 0,
              impacts: 0,
              solutions: 0,
              actions: 0,
            },
            pending: true,
          },
        },
      ]);

      (async () => {
        try {
          const response = await graphCollectionsApi.connectIssues(collectionId, {
            influencerIssueId,
            influencedIssueId,
          });
          const compoundIssueId = Number(response.compoundIssueId);
          const canvasData = await loadCollectionGraph(collectionId);
          const createdEdge = canvasData?.edges.find(
            (edge) => edge.data?.relationId === compoundIssueId
          ) ?? null;
          if (createdEdge) {
            markNewEdge(createdEdge.id);
          }
          const edgeId = createdEdge?.id ?? buildGraphRelationEdgeId(compoundIssueId);
          setSelectedEdgeId(edgeId);
          setStickyComposerEdgeId(edgeId);
          setEdgeComposerMediaFiles(null);
          toastSuccessWithUndo({
            message: graphMessage(graphLocale, "influenceConnectedToast"),
            undoLabel: graphMessage(graphLocale, "undo"),
            onUndo: async () => {
              await graphCollectionsApi.detachInfluence(collectionId, compoundIssueId);
              await loadCollectionGraph(collectionId);
            },
            undoSuccessMessage: graphMessage(graphLocale, "restoreSucceededToast"),
            undoErrorMessage: graphMessage(graphLocale, "restoreFailedToast"),
          });
        } catch (error) {
          console.error("Failed to connect issues on canvas:", error);
          toast.error(graphMessage(graphLocale, "influenceConnectError"));
          setEdges((current) => current.filter((edge) => edge.id !== optimisticEdgeId));
        }
      })();
    },
    [collectionId, graphLocale, loadCollectionGraph, markNewEdge]
  );

  const handleCloseSelectedEdge = useCallback(() => {
    clearEdgeSelection();
    setSelectedEdgeId(null);
    dismissStickyComposer();
  }, [clearEdgeSelection, dismissStickyComposer]);

  /**
   * Inspector's destructive action: soft-delete this edge's collection
   * binding (and the caller's collection-scoped notes), with an Undo toast
   * that restores both.
   */
  const handleRemoveSelectedInfluence = useCallback(async () => {
    if (!collectionId || !selectedEdge?.relationId) {
      return;
    }
    const compoundIssueId = selectedEdge.relationId;
    try {
      const response = await graphCollectionsApi.detachInfluence(collectionId, compoundIssueId);
      await loadCollectionGraph(collectionId);
      toastUndo({
        message: graphMessage(graphLocale, "removeInfluenceToast"),
        undoLabel: graphMessage(graphLocale, "undo"),
        onUndo: async () => {
          await graphCollectionsApi.restoreInfluence(collectionId, compoundIssueId, {
            deactivatedCommentIds: response.deactivatedCommentIds || [],
          });
          await loadCollectionGraph(collectionId);
        },
        undoSuccessMessage: graphMessage(graphLocale, "restoreSucceededToast"),
        undoErrorMessage: graphMessage(graphLocale, "restoreFailedToast"),
      });
    } catch (error) {
      console.error("Failed to remove influence via inspector:", error);
      toast.error(graphMessage(graphLocale, "removeInfluenceError"));
    }
  }, [collectionId, graphLocale, loadCollectionGraph, selectedEdge?.relationId]);

  /**
   * Inline delete-note action inside the multi-note inspector. Only the note
   * owner can trigger it (enforced in the inspector and by the API).
   */
  const handleDeleteSelectedNote = useCallback(
    async (noteId: number) => {
      if (!collectionId || !Number.isFinite(noteId) || noteId <= 0) {
        return;
      }
      const noteToDelete = selectedEdge?.notes.find((note) => Number(note.id) === Number(noteId));
      try {
        await api.delete(apiRoutes.comments.byId(noteId));
        await loadCollectionGraph(collectionId);
        if (!noteToDelete) {
          toast.success(graphMessage(graphLocale, "edgeNoteDeletedToast"));
          return;
        }
        toastUndo({
          message: graphMessage(graphLocale, "edgeNoteDeletedToast"),
          undoLabel: graphMessage(graphLocale, "undo"),
          onUndo: async () => {
            // Undo by re-creating an identical note. We don't have a
            // dedicated restore endpoint for individual comments today, so
            // the Undo re-posts the previous content with the same timeline
            // / place context (if any) and re-attaches its placement to the
            // original edge. A subsequent delete by the user would still
            // work because the re-created note is scoped to the same
            // collection and edge.
            if (!selectedEdge?.relationId) {
              return;
            }
            await api.post(apiRoutes.comments.byCommentable("collection", collectionId), {
              content: noteToDelete.content,
              kind: "edge_note",
              properties: noteToDelete.properties ?? {},
              placement: {
                related_type: "issue",
                related_id: selectedEdge.relationId,
                placement_type: "graph_edge_note",
              },
            });
            await loadCollectionGraph(collectionId);
          },
          undoSuccessMessage: graphMessage(graphLocale, "restoreSucceededToast"),
          undoErrorMessage: graphMessage(graphLocale, "restoreFailedToast"),
        });
      } catch (error) {
        console.error("Failed to delete edge note:", error);
        toast.error(graphMessage(graphLocale, "edgeNoteDeleteError"));
      }
    },
    [collectionId, graphLocale, loadCollectionGraph, selectedEdge?.notes, selectedEdge?.relationId]
  );

  const handlePaneClick = useCallback(() => {
    // Clicking empty canvas should behave like closing the inspector popup:
    // clear the selected edge, reset its styling, and dismiss the sticky
    // edge-note composer if it was still open.
    clearEdgeSelection();
    if (selectedEdgeId || stickyComposerEdgeId) {
      setSelectedEdgeId(null);
      dismissStickyComposer();
    }
  }, [clearEdgeSelection, dismissStickyComposer, selectedEdgeId, stickyComposerEdgeId]);

  const handleEdgeNoteSubmit = useCallback(
    async (
      content: string,
      privacy: PrivacyLevel,
      coauthors: CoauthorSuggestion[],
      _addedCoauthors: CoauthorSuggestion[],
      _removedCoauthors: CoauthorSuggestion[],
      pollDraft?: PollDraft | null,
      context?: CommentInputContext | null
    ) => {
      if (isEdgeNoteSubmitting || !collectionId || !stickyComposerEdge?.data?.relationId) {
        return;
      }

      setIsEdgeNoteSubmitting(true);
      try {
        const pollPayload = pollDraft
          ? {
              statement: pollDraft.statement,
              poll_type: pollDraft.poll_type,
              options: pollDraft.options.map((option) => ({
                option_text: option.option_text,
                option_entity_type: option.option_entity_type,
                option_entity_id: option.option_entity_id,
              })),
            }
          : undefined;

        // Graph edge note: the comment's commentable is the collection post,
        // and a placement row ties the comment to the compound issue (edge).
        // See ADR 012 / migration 2026042300_graph_collection_scoped_notes.
        const response = await api.post(apiRoutes.comments.byCommentable("collection", collectionId), {
          content,
          kind: "edge_note",
          properties: {
            privacy,
          },
          poll: pollPayload,
          place_id: context?.placeId ?? null,
          timeline_ts: context?.timelineTs ?? null,
          placement: {
            related_type: "issue",
            related_id: stickyComposerEdge.data.relationId,
            placement_type: "graph_edge_note",
          },
        });
        const createdComment = response.data as { id: number };

        try {
          await api.post(apiRoutes.votes.comment(createdComment.id), { value: 1 });
        } catch {
          /* best-effort */
        }

        if (coauthors.length > 0 && privacy !== "anonymous") {
          try {
            await api.post(apiRoutes.coauthors.request("comment", createdComment.id), {
              addedCoauthors: coauthors.map((coauthor) => ({
                id: coauthor.id,
                type: coauthor.type,
              })),
            });
          } catch {
            /* best-effort */
          }
        }

        if (edgeComposerMediaFiles && edgeComposerMediaFiles.length > 0) {
          try {
            await uploadEntityMediaDirect(
              "comment",
              createdComment.id,
              Array.from(edgeComposerMediaFiles).map((file, index) => ({
                file,
                order: index + 1,
              }))
            );
          } catch {
            /* best-effort */
          }
        }

        await loadCollectionGraph(collectionId);
        setSelectedEdgeId(stickyComposerEdge.id);
        dismissStickyComposer();
        toast.success(graphMessage(graphLocale, "edgeNoteSavedToast"));
      } catch (error) {
        console.error("Failed to save graph edge note:", error);
        toast.error(graphMessage(graphLocale, "edgeNoteSaveError"));
      } finally {
        setIsEdgeNoteSubmitting(false);
      }
    },
    [
      collectionId,
      dismissStickyComposer,
      edgeComposerMediaFiles,
      graphLocale,
      isEdgeNoteSubmitting,
      loadCollectionGraph,
      stickyComposerEdge,
    ]
  );

  const onNodesChange: OnNodesChange = useCallback(
    (changes) => {
      changes.forEach((change) => {
        if (change.type !== "position" || !change.position) {
          return;
        }
        const issueId = Number(change.id);
        if (!Number.isFinite(issueId) || issueId <= 0) {
          return;
        }
        queuePositionSave(issueId, {
          x: change.position.x,
          y: change.position.y,
        });
      });

      setNodes((current) => decorateNodes(applyNodeChanges(changes, current)));
    },
    [decorateNodes, queuePositionSave]
  );

  const onEdgesChange: OnEdgesChange = useCallback(
    (changes) => {
      setEdges((current) => applyEdgeChanges(changes, current));
    },
    []
  );

  const renderBlankCreationState = () => (
    <div className="mx-auto flex w-full max-w-lg flex-col gap-4 px-4 py-8">
      <p className="text-center text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
        {graphMessage(graphLocale, "blankSearchHint")}
      </p>
      <IssueSearch
        inputType="add"
        placeholder={graphMessage(graphLocale, "blankSearchHint")}
        className="w-full"
        prepopulateData={selectedIssue ?? undefined}
        onSelect={(suggestion) => {
          setSelectedIssue(suggestion);
        }}
        onEntityChipClose={() => setSelectedIssue(null)}
      />
      <button
        type="button"
        className={`w-full py-2 text-sm font-semibold ${
          selectedIssue && !isStartingDraft ? "btn-primary" : "btn-primary-disabled cursor-not-allowed opacity-50"
        }`}
        disabled={!selectedIssue || isStartingDraft}
        onClick={() => void handleStartFromIssue()}
      >
        {isStartingDraft ? (
          <span className="inline-flex items-center gap-2">
            <Loader2 className="size-4 animate-spin" aria-hidden />
          </span>
        ) : (
          graphMessage(graphLocale, "startFromIssue")
        )}
      </button>

      <div className="mt-8 border-t border-eoe-border-main pt-6 dark:border-eoe-dark-border-main">
        <h2 className="mb-2 text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
          {graphMessage(graphLocale, "connectGraphs")}
        </h2>
        <label
          className="mb-1 block text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub"
          htmlFor="graph-rebrew-source"
        >
          {graphMessage(graphLocale, "rebrewSourceLabel")}
        </label>
        <input
          id="graph-rebrew-source"
          type="number"
          min={1}
          className="mb-3 w-full rounded-lg border border-eoe-border-main bg-eoe-bg px-3 py-2 text-sm dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg"
          value={rebrewSourceId}
          onChange={(event) => setRebrewSourceId(event.target.value)}
        />
        <div className="mb-3 flex gap-2">
          <button
            type="button"
            className={`flex-1 rounded-lg border px-2 py-1.5 text-xs font-medium ${
              rebrewMode === "copy"
                ? "border-eoe-accent bg-eoe-accent/10 text-eoe-text dark:border-eoe-dark-accent"
                : "border-eoe-border-main dark:border-eoe-dark-border-main"
            }`}
            onClick={() => setRebrewMode("copy")}
          >
            {graphMessage(graphLocale, "rebrewCopy")}
          </button>
          <button
            type="button"
            className={`flex-1 rounded-lg border px-2 py-1.5 text-xs font-medium ${
              rebrewMode === "sync"
                ? "border-eoe-accent bg-eoe-accent/10 text-eoe-text dark:border-eoe-dark-accent"
                : "border-eoe-border-main dark:border-eoe-dark-border-main"
            }`}
            onClick={() => setRebrewMode("sync")}
          >
            {graphMessage(graphLocale, "rebrewSync")}
          </button>
        </div>
        <button
          type="button"
          className="btn-secondary w-full py-2 text-sm font-semibold"
          disabled={rebrewBusy}
          onClick={() => void handleRebrew()}
        >
          {rebrewBusy ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="size-4 animate-spin" aria-hidden />
            </span>
          ) : (
            graphMessage(graphLocale, "rebrewSubmit")
          )}
        </button>
      </div>
    </div>
  );

  const renderLoadedCollection = () => {
    const isDraft = loadedCollection?.status === "draft";
    return (
      <div className="flex min-h-0 w-full flex-1 flex-col">
        {/*
         * Title + details panel. Title input is the always-visible header
         * of the DetailPanel; the chevron expands to reveal the rest of
         * the collection form (body, media, privacy, people) PLUS the
         * per-action buttons (Delete / Save Draft / Publish / Save). The
         * DetailPanel owns the open/close state so we don't double up on
         * expand affordances, and actions live inside the panel so they
         * only appear when the user actually wants to manage the
         * collection (see screenshots in the ADR follow-up).
         */}
        <div
          id="graph-editor-details-panel"
          className="flex-shrink-0 border-b border-eoe-border-main bg-eoe-bg dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg"
        >
          <div className="flex items-center gap-2 px-4 pt-3">
            <div className="min-w-0 flex-1">
              <InputField
                id="graph-title"
                name="graph-title"
                labelText={graphMessage(graphLocale, "graphTitleLabel")}
                fieldPlaceholder={graphMessage(graphLocale, "graphTitleLabel")}
                value={headerTitleValue}
                onChange={(event) => setHeaderTitleValue(event.target.value)}
                onFocus={() => setIsTitleFocused(true)}
                onBlur={() => {
                  void handleHeaderTitleBlur();
                }}
                validationState={headerTitleValue.trim().length > 0 ? "valid" : "none"}
                className="!min-h-[48px] !rounded-xl"
              />
            </div>
            {isDraft ? (
              <span className="rounded-full bg-eoe-icon px-2 py-0.5 text-[11px] font-semibold text-eoe-text-sub dark:bg-eoe-dark-icon dark:text-eoe-dark-text-sub">
                {graphMessage(graphLocale, "draftBadge")}
              </span>
            ) : null}
            {isSavingTitle ? (
              <Loader2 className="size-4 animate-spin text-eoe-text-sub dark:text-eoe-dark-text-sub" aria-hidden />
            ) : null}
          </div>
          {loadedCollection ? (
            <DetailPanel
              open={detailsPanelOpen}
              onOpenChange={setDetailsPanelOpen}
              showTopCollapse={false}
              showBottomCollapse={true}
              className="!px-4 !pb-3 !pt-1"
            >
              <div className="flex flex-col gap-3 pb-2 text-left">
                <CollectionPostForm
                  ref={detailsFormRef}
                  mode="edit"
                  collectionId={collectionId}
                  initialCollection={loadedCollection as CollectionPostResponse}
                  entity={null}
                  onSuccess={handleDetailsSaved}
                  onCancel={() => setDetailsPanelOpen(false)}
                  extraCollectionProperties={{
                    collection_type: "graph",
                  }}
                  onDirtyChange={setDetailsDirty}
                  hideTitle
                  hideActions
                />
                {/*
                 * Collection-level actions. Moved inside the DetailPanel
                 * (per screenshot 3) so they only surface when the user is
                 * actively managing the collection; the canvas stays
                 * focused on graph editing otherwise.
                 */}
                <div className="flex items-center justify-between gap-2 border-t border-eoe-border-main pt-2 dark:border-eoe-dark-border-main">
                  <button
                    type="button"
                    onClick={() => setDeleteConfirmOpen(true)}
                    disabled={isDeleting || isPublishing || isSavingDraft}
                    className="inline-flex items-center gap-1.5 rounded-full border border-warning-red/60 px-3 py-1.5 text-xs font-semibold text-warning-red transition-colors hover:bg-warning-red/10 disabled:cursor-not-allowed disabled:opacity-50 dark:border-dark-warning-red/60 dark:text-dark-warning-red dark:hover:bg-dark-warning-red/10"
                    aria-label={graphMessage(graphLocale, "delete")}
                  >
                    <Trash2 className="size-3.5" aria-hidden />
                    <span>{graphMessage(graphLocale, "delete")}</span>
                  </button>
                  <div className="flex items-center gap-2">
                    {isDraft ? (
                      <>
                        <button
                          type="button"
                          onClick={() => void handleSaveDraft()}
                          disabled={isSavingDraft || isPublishing || isDeleting}
                          className="btn-secondary inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {isSavingDraft ? (
                            <Loader2 className="size-3.5 animate-spin" aria-hidden />
                          ) : (
                            <Save className="size-3.5" aria-hidden />
                          )}
                          {graphMessage(graphLocale, "saveDraft")}
                        </button>
                        <button
                          type="button"
                          onClick={() => void handlePublish()}
                          disabled={
                            isPublishing
                            || isDeleting
                            || isSavingDraft
                            || headerTitleValue.trim().length === 0
                          }
                          className="btn-primary inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-50"
                          title={
                            headerTitleValue.trim().length === 0
                              ? graphMessage(graphLocale, "titleRequiredForPublish")
                              : undefined
                          }
                        >
                          {isPublishing ? (
                            <Loader2 className="size-3.5 animate-spin" aria-hidden />
                          ) : (
                            <Upload className="size-3.5" aria-hidden />
                          )}
                          {graphMessage(graphLocale, "publish")}
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => void handleSaveDraft()}
                        disabled={isSavingDraft || isDeleting}
                        className="btn-primary inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {isSavingDraft ? (
                          <Loader2 className="size-3.5 animate-spin" aria-hidden />
                        ) : (
                          <Save className="size-3.5" aria-hidden />
                        )}
                        {graphMessage(graphLocale, "save")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </DetailPanel>
          ) : null}
        </div>
        <div className="border-b border-eoe-border-main px-4 py-3 dark:border-eoe-dark-border-main">
          <div className="flex flex-col gap-3 md:flex-row md:items-end">
            <div className="min-w-0 flex-1">
              <IssueSearch
                inputType="add"
                placeholder={graphMessage(graphLocale, "addIssueSearchPlaceholder")}
                className="w-full"
                prepopulateData={canvasIssueToAdd ?? undefined}
                onSelect={(suggestion) => setCanvasIssueToAdd(suggestion)}
                onEntityChipClose={() => setCanvasIssueToAdd(null)}
              />
            </div>
            <button
              type="button"
              className={`w-full rounded-full px-4 py-2 text-sm font-semibold md:w-auto ${
                canvasIssueToAdd && !isAddingCanvasIssue
                  ? "btn-secondary"
                  : "btn-primary-disabled cursor-not-allowed opacity-50"
              }`}
              disabled={!canvasIssueToAdd || isAddingCanvasIssue}
              onClick={() => void handleAddLooseIssue()}
            >
              {isAddingCanvasIssue ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                </span>
              ) : (
                graphMessage(graphLocale, "addIssueToGraph")
              )}
            </button>
            {isAnalysisEnabled && collectionId ? (
              <button
                type="button"
                onClick={() => setIsAnalysisOpen((current) => !current)}
                aria-expanded={isAnalysisOpen}
                aria-controls="graph-analysis-panel"
                className={`inline-flex w-full items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-semibold md:w-auto ${
                  isAnalysisOpen ? "btn-primary" : "btn-secondary"
                }`}
              >
                <Sparkles className="size-4" aria-hidden />
                <span>{analyzeGraphCopy.analyzeAction}</span>
              </button>
            ) : null}
          </div>
        </div>
        {snapshotError ? (
          <div className="px-4 py-2 text-sm text-warning-red dark:text-warning-red-dark" role="alert">
            {snapshotError}
          </div>
        ) : null}
        <div className="relative min-h-0 flex-1">
          {isSnapshotLoading ? (
            <LoadingSkeleton
              variant="graph"
              ariaLabel={graphMessage(graphLocale, "loadingGraph")}
              containerClassName="graph-explorer-edit-loading"
            />
          ) : (
            <>
              <GraphCanvas
                nodes={nodes}
                edges={decoratedEdges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                depth={0}
                onDepthUp={noopDepth}
                onDepthDown={noopDepth}
                showDepthControls={false}
                locale={graphLocale}
                onEdgeClick={handleEdgeClick}
                onPaneClick={handlePaneClick}
                rootNodeId={rootNodeId}
                editable={Boolean(collectionId)}
                onConnect={handleCanvasConnect}
                /*
                 * Editor owns edge selection so the inspector stays in
                 * sync with canvas highlight state (new edge promotion,
                 * sticky composer, remove-influence action). Disabling
                 * the canvas-internal reducer avoids fighting two stores
                 * for the same state.
                 */
                manageEdgeSelection={false}
              />
              {isRemix ? (
                <aside
                  className="pointer-events-auto absolute left-4 top-4 z-10 flex max-w-[min(320px,calc(100%-32px))] flex-col gap-1 rounded-2xl border border-eoe-border-main bg-eoe-bg-modal/95 p-3 shadow-md backdrop-blur dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-modal/95"
                  aria-label={graphMessage(graphLocale, "remixHeader")}
                >
                  <p className="text-xs font-semibold uppercase tracking-wide text-eoe-text-sub dark:text-eoe-dark-text-sub">
                    {graphMessage(graphLocale, "remixHeader")}
                  </p>
                  <p className="text-[11px] text-eoe-text-sub dark:text-eoe-dark-text-sub">
                    {graphMessage(graphLocale, "remixBridgeHint")}
                  </p>
                  <div className="mt-1 flex flex-col gap-1">
                    {sources
                      .filter((source) => source.role === "source")
                      .map((source) => {
                        const color = sourceColorById.get(source.collectionId)
                          ?? SOURCE_PALETTE[0];
                        const actorName =
                          source.actorKind === "anonymous"
                            ? graphMessage(graphLocale, "sourceAnonymousActor")
                            : (source.actorName || graphMessage(graphLocale, "sourceAnonymousActor"));
                        const title =
                          source.title || graphMessage(graphLocale, "untitledGraph");
                        return (
                          <div
                            key={String(source.collectionId)}
                            className="flex items-center gap-2 text-xs"
                            aria-label={formatGraphMessage(graphLocale, "remixSourceChipAria", {
                              title,
                              actor: actorName,
                            })}
                          >
                            <span
                              aria-hidden
                              className="inline-block size-2.5 rounded-full"
                              style={{ background: color }}
                            />
                            <span className="truncate text-eoe-text dark:text-eoe-dark-text">
                              {title}
                            </span>
                            <span className="truncate text-eoe-text-sub dark:text-eoe-dark-text-sub">
                              {actorName}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                </aside>
              ) : null}
              <LiveAnnouncer message={liveAnnouncement} />
              {selectedEdge && !isAnalysisOpen ? (
                <GraphEdgeInspector
                  edge={selectedEdge}
                  locale={graphLocale}
                  onClose={handleCloseSelectedEdge}
                  onRemoveInfluence={
                    collectionId && selectedEdge.relationId
                      ? () => void handleRemoveSelectedInfluence()
                      : undefined
                  }
                  onDeleteNote={(noteId) => void handleDeleteSelectedNote(noteId)}
                  viewerUserId={viewerUserId}
                />
              ) : null}
              {isAnalysisEnabled && isAnalysisOpen && collectionId ? (
                <div id="graph-analysis-panel" className="absolute inset-0 pointer-events-none">
                  <GraphAnalysisPanel
                    collectionId={collectionId}
                    nodeCount={nodes.length}
                    edgeCount={edges.length}
                    onClose={() => setIsAnalysisOpen(false)}
                    onSuggestionAccepted={() => {
                      void loadCollectionGraph(collectionId);
                    }}
                    onFocusNode={(nodeId) => {
                      setNodes((current) =>
                        current.map((node) => ({
                          ...node,
                          selected: Number(node.id) === nodeId,
                        }))
                      );
                    }}
                  />
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    );
  };

  /**
   * When editing a loaded graph collection on mobile, we take over the whole
   * viewport (covers the app bar + graph tabs) so the user can focus on the
   * canvas without chrome competing for space. Desktop keeps the in-page
   * layout with tab navigation.
   */
  const isMobileFullscreen = isMobile && Boolean(collectionId);

  /*
   * Mobile fullscreen mode hides the AppBar globally via the shared
   * `fullscreen-viewer-active` body class so the canvas owns the whole
   * viewport (no nav chrome competing for vertical space on touch devices).
   * Reference-counted through `data-fullscreen-viewer-count` so a nested
   * `GraphFullscreenViewer` doesn't fight this toggle.
   */
  useEffect(() => {
    if (!isMobileFullscreen) {
      return;
    }
    const body = document.body;
    const currentCount = Number(body.dataset.fullscreenViewerCount ?? "0");
    const nextCount = currentCount + 1;
    body.dataset.fullscreenViewerCount = String(nextCount);
    body.classList.add(FULLSCREEN_VIEWER_BODY_CLASS);
    return () => {
      const count = Number(body.dataset.fullscreenViewerCount ?? "1");
      const next = Math.max(count - 1, 0);
      if (next === 0) {
        delete body.dataset.fullscreenViewerCount;
        body.classList.remove(FULLSCREEN_VIEWER_BODY_CLASS);
      } else {
        body.dataset.fullscreenViewerCount = String(next);
      }
    };
  }, [isMobileFullscreen]);

  const rootClassName = isMobileFullscreen
    ? "graph-explorer-edit fixed inset-0 flex min-h-0 flex-col bg-eoe-bg dark:bg-eoe-dark-bg"
    : "graph-explorer-edit flex h-[calc(100vh-56px)] min-h-0 flex-col";

  return (
    <div
      className={rootClassName}
      style={
        isMobileFullscreen
          ? { height: "100dvh", zIndex: LAYER_TOKENS.modal }
          : undefined
      }
    >
      {!isMobileFullscreen ? (
        <ExperimentalFeatureBanner featureId="graph-explorer" />
      ) : null}
      {!isMobileFullscreen ? (
        <nav
          className="flex flex-wrap items-center gap-3 border-b border-eoe-border-main bg-eoe-bg-modal/90 px-4 py-2 dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-modal/90"
          aria-label={graphMessage(graphLocale, "editorTab")}
        >
          <Link
            to="/graph"
            className="text-sm font-semibold text-eoe-text-sub hover:text-eoe-text dark:text-eoe-dark-text-sub dark:hover:text-eoe-dark-text"
          >
            {graphMessage(graphLocale, "exploreTab")}
          </Link>
          <span className="text-eoe-text-sub dark:text-eoe-dark-text-sub" aria-hidden>
            |
          </span>
          <Link
            to="/graph?mode=feed"
            className="text-sm font-semibold text-eoe-text-sub hover:text-eoe-text dark:text-eoe-dark-text-sub dark:hover:text-eoe-dark-text"
          >
            {graphMessage(graphLocale, "feedTab")}
          </Link>
          <span className="text-eoe-text-sub dark:text-eoe-dark-text-sub" aria-hidden>
            |
          </span>
          <span
            className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text"
            aria-current="page"
          >
            {graphMessage(graphLocale, "editorTab")}
          </span>
        </nav>
      ) : (
        <div className="flex flex-shrink-0 items-center gap-2 border-b border-eoe-border-main bg-eoe-bg px-3 py-2 dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg">
          <button
            type="button"
            onClick={() => navigate("/graph?mode=feed")}
            className="flex size-8 items-center justify-center rounded-full text-eoe-text hover:bg-eoe-bg-hover dark:text-eoe-dark-text dark:hover:bg-eoe-dark-bg-hover"
            aria-label={graphMessage(graphLocale, "closeEdgeInspector")}
          >
            <X className="size-5" aria-hidden />
          </button>
          <span className="flex-1 truncate text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
            {graphMessage(graphLocale, "graphCollectionTitle")}
          </span>
        </div>
      )}

      {collectionId ? renderLoadedCollection() : renderBlankCreationState()}

      {collectionId && stickyComposerEdge && selectedEdge && selectedEdge.id === stickyComposerEdge.id ? (
        <div className="sticky bottom-0 z-30 border-t border-eoe-border-main bg-eoe-bg-modal/95 shadow-lg backdrop-blur dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-modal/95">
          <div className="mx-auto flex w-full max-w-5xl flex-col gap-3 px-4 py-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-eoe-text-sub dark:text-eoe-dark-text-sub">
                  {graphMessage(graphLocale, "edgeInspectorTitle")}
                </p>
                <h2 className="mt-1 text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
                  {`${selectedEdge.sourceName} -> ${selectedEdge.targetName}`}
                </h2>
              </div>
              <button
                type="button"
                onClick={dismissStickyComposer}
                className="btn-secondary rounded-full px-3 py-1.5 text-xs font-semibold"
              >
                {graphMessage(graphLocale, "dismissComposer")}
              </button>
            </div>

            <CommentInput
              action="add"
              buttonText={isEdgeNoteSubmitting ? "..." : graphMessage(graphLocale, "edgeNoteSubmit")}
              cancelText={graphMessage(graphLocale, "dismissComposer")}
              placeholder={graphMessage(graphLocale, "stickyCommentHint")}
              autoFocus
              isMobile={isMobile}
              showTimelinePlace={true}
              onSubmit={handleEdgeNoteSubmit}
              onCancel={dismissStickyComposer}
              onMediaSelected={setEdgeComposerMediaFiles}
            />
          </div>
        </div>
      ) : null}

      {/*
       * The old Details Modal has been replaced by an inline DetailPanel
       * inside `renderLoadedCollection` — we keep the unsaved-changes confirm
       * dialog so users who close the panel while editing are prompted.
       */}
      <DialogConfirmation
        title={graphMessage(graphLocale, "saveChangesTitle")}
        description={graphMessage(graphLocale, "saveChangesDescription")}
        onConfirm={handleDetailsSaveAndClose}
        onCancel={handleDetailsDiscard}
        onDismiss={() => setDetailsCloseConfirmOpen(false)}
        isOpen={detailsCloseConfirmOpen}
        confirmText={graphMessage(graphLocale, "saveChangesConfirm")}
        cancelText={graphMessage(graphLocale, "discardChanges")}
      />

      {/* Delete confirmation */}
      <DialogConfirmation
        title={graphMessage(graphLocale, "deleteConfirmationTitle")}
        description={graphMessage(graphLocale, "deleteConfirmationBody")}
        onConfirm={() => void handleDelete()}
        onCancel={() => setDeleteConfirmOpen(false)}
        onDismiss={() => setDeleteConfirmOpen(false)}
        isOpen={deleteConfirmOpen}
        confirmText={graphMessage(graphLocale, "delete")}
        cancelText={graphMessage(graphLocale, "discardChanges")}
      />

      {collectionId && addInfluenceOpen && addInfluenceCenterIssue ? (
        <GraphAddInfluenceSurface
          isOpen={addInfluenceOpen}
          onClose={handleAddInfluenceClose}
          direction={addInfluenceDirection}
          centerIssue={addInfluenceCenterIssue}
          /*
           * Only exclude the center issue itself to prevent self-influence.
           * Issues already on the canvas should still appear in search
           * results — the add path is idempotent (re-adding an on-canvas
           * issue just creates the influence edge), and for users who
           * already know the issue is on the canvas, dragging between
           * visible nodes is the faster path.
           */
          excludedIssueIds={[]}
          onAdded={handleInfluenceAdded}
          locale={graphLocale}
        />
      ) : null}
    </div>
  );
};

export default GraphExplorerEdit;
